/**
 * Turns instructor-typed plain text into display HTML: URLs become clickable anchors and
 * newlines survive as <br>.
 *
 * Nothing is stored in HTML — this runs on the read path only, so every note ever written
 * gets clickable links without a migration, and the textarea/dictation editor is untouched.
 *
 * Only `http://`, `https://` and `www.` are treated as links. Bare hostnames (`example.com`)
 * are deliberately NOT matched: "i.e. see the syllabus" style prose throws too many false
 * positives, and a wrong link is worse than a missing one.
 */

/**
 * A run of non-space characters starting at a scheme or `www.`.
 * Trailing punctuation is stripped afterwards by `trimTrailingPunctuation` rather than being
 * excluded here — where a character is legal inside a URL *and* common at the end of a
 * sentence, only position can tell the two apart.
 */
const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s]+/gi;

/** Closers that should only stay attached when the URL itself opened them, e.g. Wikipedia's `(disambiguation)`. */
const BRACKET_PAIRS: ReadonlyArray<readonly [string, string]> = [
	['(', ')'],
	['[', ']'],
	['{', '}']
];

/**
 * Characters that end a URL only by accident: sentence punctuation, and the quotes an
 * instructor wraps a pasted address in. All are legal mid-URL, so only trailing ones go.
 */
const TRAILING_PROSE_CHARACTERS = '.,;:!?"\'';

function escapeHtml(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function countOccurrences(value: string, char: string): number {
	let count = 0;
	for (const current of value) {
		if (current === char) {
			count++;
		}
	}
	return count;
}

/**
 * Walks back off the end of a matched URL while the last character reads as sentence
 * punctuation rather than part of the address.
 *
 * `See https://example.com/a.` -> the period is prose.
 * `https://en.wikipedia.org/wiki/Foo_(bar)` -> the paren belongs to the URL, because the URL opened it.
 */
function trimTrailingPunctuation(url: string): string {
	let result = url;

	for (;;) {
		const last = result.at(-1);

		if (!last) {
			return result;
		}

		if (TRAILING_PROSE_CHARACTERS.includes(last)) {
			result = result.slice(0, -1);
			continue;
		}

		const unbalanced = BRACKET_PAIRS.find(
			([open, close]) => last === close && countOccurrences(result, open) < countOccurrences(result, close)
		);
		if (unbalanced) {
			result = result.slice(0, -1);
			continue;
		}

		return result;
	}
}

/** Escapes a plain-text run and preserves its line breaks. */
function escapeWithLineBreaks(value: string): string {
	return escapeHtml(value).replace(/\r\n|\r|\n/g, '<br />');
}

/**
 * Renders `value` as HTML safe to hand to Angular's `[innerHTML]`.
 *
 * Text is escaped *before* any markup is added, and hrefs can only ever be `http`/`https`
 * because those are the only forms the pattern matches — so there is no `javascript:` path
 * through here. Angular's sanitizer still runs on the result as a second layer.
 */
export function linkifyToHtml(value: string | null | undefined): string {
	if (!value) {
		return '';
	}

	let html = '';
	let cursor = 0;

	URL_PATTERN.lastIndex = 0;

	let match: RegExpExecArray | null;
	while ((match = URL_PATTERN.exec(value)) !== null) {
		const url = trimTrailingPunctuation(match[0]);

		// Trimming can leave a bare scheme behind (`https://.` -> `https://`). That is not a link,
		// so fall through and let it render as ordinary text.
		if (!url.replace(/^https?:\/\/|^www\./i, '')) {
			continue;
		}

		html += escapeWithLineBreaks(value.slice(cursor, match.index));

		const href = url.toLowerCase().startsWith('www.') ? `https://${url}` : url;
		const safeHref = escapeHtml(href);
		html += `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" title="${safeHref}">${escapeHtml(url)}</a>`;

		cursor = match.index + url.length;
		// Trimmed punctuation has to be re-scanned as prose, so rewind the pattern to the cut.
		URL_PATTERN.lastIndex = cursor;
	}

	return html + escapeWithLineBreaks(value.slice(cursor));
}
